const capitalizeFirstLetter = (word) =>
  word.charAt(0).toUpperCase() + word.substring(1);

const MatchCenterHelpers = {
  getSponsor: (match) => {
    const sponsor = {
      name: { label: '', value: '' },
      image: '',
      companyLogo: '',
    };
    if (!match.settings) {
      return sponsor;
    }
    if (match.settings.settings?.sponsor) {
      sponsor.name = {
        label: match.settings.settings.sponsor.name,
        value: match.settings.settings.sponsor.id,
      };
      sponsor.image = match.settings.settings.sponsor.image;
      sponsor.companyLogo = match.settings.settings.sponsor.companyLogo;
    }
    return sponsor;
  },
  getCompany: (match, companies) => {
    const company = { label: '', value: '' };
    if (!companies) return company;
    const index = companies.findIndex(
      (c) => c.id === match.playerRatingCompany?.companyId
    );
    if (index !== -1) {
      company.label = companies[index].name;
      company.value = companies[index].id;
    }
    return company;
  },
  getBannerOptions: (match) => {
    const options = match.settings?.settings?.bannerOptions;
    return {
      gamecenter: options?.gamecenter ?? true,
      mvp: options?.mvp ?? true,
      playpredict: options?.playpredict ?? false,
      lineup: options?.lineup ?? true,
    };
  },
  getMatchCenterOptions: (match) => {
    const options = match.settings?.settings?.matchCenterOptions;
    return {
      showTickets: options ? options.tickets : true,
      showMatchSponsor: options ? options.matchSponsor : false,
    };
  },
  addDash: (value) => (value !== '' && value ? value : '-'),
  getLinks: (match) =>
    match.settings?.settings?.links?.map((link) => ({
      link_name: link.name,
      link_link: link.link,
      link_type: { label: capitalizeFirstLetter(link.type), value: link.type },
    })) || [],
  getTicket: (match) => {
    const ticket = { ticket_name: '', ticket_link: '', externalId: '' };
    if (match.settings?.settings?.ticket) {
      const matchTicket = match.settings?.settings?.ticket;
      ticket.ticket_name = matchTicket.name;
      ticket.ticket_link = matchTicket.link;
      ticket.externalId = matchTicket.externalId;
    }
    return ticket;
  },
  // Rebuilds the full settings payload the single-match edit form would
  // submit (updateUpcomingMatch replaces the whole settings object, it
  // doesn't patch), so callers that only want to change one or two fields
  // - like a bulk edit - still have to resend everything else unchanged.
  // `overrides.sponsorCompany` is a full company record (id/name/images),
  // `overrides.prSponsorCompanyId` is just a company id, `overrides.
  // visibleInBanner`/`gamecenter`/`mvp`/`playpredict`/`lineup` are booleans
  // - all optional.
  buildSettingsPayload: (match, companies, teams, overrides = {}) => {
    const { name, image, companyLogo } = MatchCenterHelpers.getSponsor(match);
    const company = MatchCenterHelpers.getCompany(match, companies);
    const bannerOptions = MatchCenterHelpers.getBannerOptions(match);
    const matchCenterOptions = MatchCenterHelpers.getMatchCenterOptions(match);
    const ticket = MatchCenterHelpers.getTicket(match);
    const links = MatchCenterHelpers.getLinks(match);
    const teamId = !match.isCustom
      ? ((teams || []).find(
          (team) =>
            team.id === match.homeTeam?.id || team.id === match.awayTeam?.id
        )?.id ?? '')
      : '';
    const {
      sponsorCompany,
      prSponsorCompanyId,
      visibleInBanner,
      gamecenter,
      mvp,
      playpredict,
      lineup,
      prizes,
    } = overrides;
    return {
      visibleInBanner:
        visibleInBanner !== undefined
          ? visibleInBanner
          : (match.settings?.visibleInBanner ?? false),
      sponsorId: sponsorCompany ? sponsorCompany.id : name.value,
      sponsorName: sponsorCompany ? sponsorCompany.name : name.label,
      sponsorImage: sponsorCompany
        ? sponsorCompany.images?.appTicketImage || ''
        : image,
      companyLogo: sponsorCompany
        ? sponsorCompany.images?.companyImage || ''
        : companyLogo,
      gamecenter:
        gamecenter !== undefined ? gamecenter : bannerOptions.gamecenter,
      mvp: mvp !== undefined ? mvp : bannerOptions.mvp,
      playpredict:
        playpredict !== undefined ? playpredict : bannerOptions.playpredict,
      lineup: lineup !== undefined ? lineup : bannerOptions.lineup,
      tickets: matchCenterOptions.showTickets,
      matchSponsor: matchCenterOptions.showMatchSponsor,
      playerRatingCompany: {
        companyId:
          prSponsorCompanyId !== undefined ? prSponsorCompanyId : company.value,
        teamId,
        type: 'single',
      },
      ticket: ticket.ticket_name
        ? {
            name: ticket.ticket_name,
            link: ticket.ticket_link,
            externalId: ticket.externalId,
          }
        : {},
      links: links.map((link) => ({
        name: link.link_name,
        link: link.link_link,
        type: 'external',
      })),
      prizes:
        prizes !== undefined ? prizes : match.settings?.settings?.prizes || [],
    };
  },
};

export default MatchCenterHelpers;
